<template>
  <v-card data-testid="regimen_edit">
    <v-card-title style="padding: 0px">
      <v-toolbar>
        <v-btn icon @click="close" data-testid="regimen_edit_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>
          <span>{{ $t("common.exercises_for", { name_given: user_name, name_family: ""} ) }}</span>
        </v-toolbar-title>
        <v-spacer />
        <pre v-if="admin_show_ids && regimen"> {{ regimen.id }}</pre>
      </v-toolbar>
    </v-card-title>
    <v-card-text class="pt-6">
      <v-alert
        v-model="show_ui_changes_alert"
        type="info"
        dismissible
        class="mb-8"
        data-testid="regimen_edit_ui_changes_alert"
        @input="persist_ui_changes_alert_dismiss"
      >
        <i18n path="regimen_edit.ui_changes_alert" tag="span">
          <template v-slot:learn_more>
            <a
              href="#"
              class="white--text font-weight-bold"
              data-testid="regimen_edit_ui_changes_learn_more"
              @click.prevent="ui_changes_modal = true"
            >{{ $t("regimen_edit.ui_changes_learn_more") }}</a>
          </template>
        </i18n>
      </v-alert>

      <v-layout row wrap justify-space-around>
        <v-flex xs12 md5>
          <v-select
            v-model="regimen.owner_id"
            outlined
            :label="$t('common.assigned_to_label')"
            :hint="$t('regimen_edit.the_primary_contact_person_for_user_regimen')"
            :items="owner_options"
            @change="unsaved_data = true"
          />

          <v-textarea
            v-model="regimen.instructions"
            outlined
            auto-grow
            rows="4"
            :label="$t('common.special_instructions')"
            :hint="$t('regimen_edit.any_special_instructions')"
            @change="unsaved_data = true"
          />

          <v-expansion-panels v-model="tools_permissions_panel">
            <v-expansion-panel v-if="regimen">
              <v-expansion-panel-header>{{ $t("regimen_edit.tools_and_permissions") }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-checkbox
                  v-model="regimen.permissions.view_printables"
                  dense
                  :indeterminate="regimen.permissions.view_printables === null || regimen.permissions.view_printables === undefined"
                  :indeterminate-icon="default_allow_view_printables ? 'mdi-check-box-outline' : 'mdi-checkbox-blank-outline'"
                  :label="$t('regimen_edit.allow_view_printables')"
                />

                <v-checkbox
                  v-model="regimen.permissions.view_tools"
                  dense
                  :indeterminate="regimen.permissions.view_tools === null || regimen.permissions.view_tools === undefined"
                  :indeterminate-icon="default_allow_view_tools ? 'mdi-check-box-outline' : 'mdi-checkbox-blank-outline'"
                  :label="$t('regimen_edit.allow_view_tools')"
                />

                <v-checkbox
                  v-model="regimen.permissions.view_progress_report"
                  dense
                  :indeterminate="regimen.permissions.view_progress_report === null || regimen.permissions.view_progress_report === undefined"
                  :indeterminate-icon="default_allow_progress_report ? 'mdi-check-box-outline' : 'mdi-checkbox-blank-outline'"
                  :label="$t('regimen_edit.allow_progress_report')"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-flex>
        <v-flex xs12 md6 class="pt-3">
          <ExerciseListEdit v-model="regimen.exercises" @edit="edit" @add="add" @delete="unsaved_data = true" @dragged="unsaved_data = true" />
        </v-flex>
      </v-layout>
    </v-card-text>

    <v-dialog v-model="dialog" max-width="1200">
      <RegimenExerciseMetaEdit
        v-if="dialog"
        v-model="current_regimen_exercise_edit"
        context="regimen"
        :allowed_exercise_types="allowed_exercise_types"
        :assigned_exercises="regimen.exercises"
        @save="save_exercise"
      />
    </v-dialog>

    <v-dialog v-model="ui_changes_modal" max-width="700">
      <v-card v-if="ui_changes_modal" data-testid="regimen_edit_ui_changes_modal">
        <v-card-title class="headline">{{ $t("regimen_edit.ui_changes_modal_title") }}</v-card-title>
        <v-card-text>
          <p>{{ $t("regimen_edit.ui_changes_modal_intro") }}</p>

          <iframe class='sproutvideo-player' src='https://videos.sproutvideo.com/embed/109ad3bd1e18e1c09a/6ab36f0a741cde5f' width='640' height='446' frameborder='0' allowfullscreen referrerpolicy='no-referrer-when-downgrade' title='Video Player'></iframe>

          <ul class="mt-3 pl-4">
            <li class="mb-3">{{ $t("regimen_edit.ui_changes_modal_assignment") }}</li>
            <li class="mb-3">{{ $t("regimen_edit.ui_changes_modal_exercises") }}</li>
            <li>{{ $t("regimen_edit.ui_changes_modal_grids") }}</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" data-testid="regimen_edit_ui_changes_modal_close" @click="ui_changes_modal = false">
            {{ $t("common.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-actions>
      <v-alert v-if="error_message" type="error" outlined dense style="margin-left: 20px">{{ error_message }}</v-alert>
      <p v-else-if="unsaved_data && regimen.exercises.length > 0" style="margin-left: 20px">{{ $t("regimen_edit.edited_unsaved_changes") }}</p>
      <v-spacer />
      <v-btn text @click="discard_changes" data-testid="regimen_edit_discard_changes">{{ $t("regimen_edit.discard_changes") }}</v-btn>
      <v-btn color="primary" :disabled="regimen.exercises.length == 0 || in_flight" @click="submit" data-testid="regimen_edit_save_exercises">{{ $t("regimen_edit.save_exercises") }}</v-btn>
    </v-card-actions>

    <v-progress-linear v-if="in_flight" :indeterminate="true" />
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import Vue from "vue";
import axios from "axios";
import uuid from "uuid";
import RegimenExerciseMetaEdit from "./RegimenExerciseMetaEdit.vue";
import ExerciseListEdit from "./ExerciseListEdit";
import { ensureValidGridScheduleStage } from "../lib/regimen_grid_customization";

const UI_CHANGES_ALERT_STORAGE_KEY = "opticalgym_regimen_edit_ui_changes_alert_dismissed";

function isUiChangesAlertDismissed() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  return window.localStorage.getItem(UI_CHANGES_ALERT_STORAGE_KEY) === "1";
}

export default {
  components: { ExerciseListEdit, RegimenExerciseMetaEdit },
  props: {
    invite_id: {
      type: String,
      required: true,
    },
    user_name: {
      type: String,
      required: true,
    },
    current_user: {
      type: Object,
      required: true,
    },
    clinic: {
      type: Object,
      required: true,
    },
    // List of staff members that can be used to construct possible owners
    staff: {
      type: Array,
      required: true,
    },
    exercises: {
      type: Object,
      required: true,
    },
    video_exercises: {
      type: Object,
      required: true,
    },
    invite_owner_id: {
      // Might be '' if unknown
      type: String,
      required: true,
    },
  },
  data() {
    let data = {
      dialog: false,
      allowed_exercise_types: undefined,
      unsaved_data: false,
      in_flight: false,
      error_message: "",
      regimen: {
        instructions: "",
        exercises: [],
        owner_id: "",
        permissions: {},
      },
      current_regimen_exercise_edit: {
        exercise_id: null,
        level: 0,
        instructions: "",
        config: {},
      },
      tools_permissions_panel: false,
      show_ui_changes_alert: !isUiChangesAlertDismissed(),
      ui_changes_modal: false,
    };

    return data;
  },
  computed: {
    ...mapState(["admin_show_ids", "current_clinic_prefs"]),
    default_allow_view_tools() {
      if (this.current_clinic_prefs && this.current_clinic_prefs.default_user_perms) {
        return this.current_clinic_prefs.default_user_perms.view_tools;
      } else if (this.clinic.has_tag("default_allow_view_tools")) {
        return this.clinic.tag_value("default_allow_view_tools");
      } else {
        return true;
      }
    },
    default_allow_view_printables() {
      if (this.current_clinic_prefs && this.current_clinic_prefs.default_user_perms) {
        return this.current_clinic_prefs.default_user_perms.view_printables;
      } else if (this.clinic.has_tag("default_allow_view_printables")) {
        return this.clinic.tag_value("default_allow_view_printables");
      } else {
        return true;
      }
    },
    default_allow_progress_report() {
      if (this.current_clinic_prefs && this.current_clinic_prefs.default_user_perms) {
        return this.current_clinic_prefs.default_user_perms.view_progress_report;
      } else if (this.clinic.has_tag("default_allow_progress_report")) {
        return this.clinic.tag_value("default_allow_progress_report");
      } else {
        return false;
      }
    },
    owner_options() {
      let options = [];
      for (var i in this.staff) {
        let staff = this.staff[i];
        options.push({
          value: staff.id,
          text: staff.name_given + " " + staff.name_family,
        });
      }

      // Sort by name -- todo sort by last-name?
      options.sort((a, b) => a.text.localeCompare(b.text));

      return options;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    persist_ui_changes_alert_dismiss(visible) {
      if (!visible && typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(UI_CHANGES_ALERT_STORAGE_KEY, "1");
      }
    },
    close() {
      this.$emit("close", {});
    },
    save_exercise() {
      const exercise = JSON.parse(JSON.stringify(this.current_regimen_exercise_edit));
      if (this.container_source_duplicate(exercise)) {
        this.error_message = this.$t("regimen_edit.container_already_assigned");
        return;
      }

      this.dialog = false;
      if (exercise.new) {
        this.regimen.exercises.push(exercise);
      } else {
        Vue.set(this.regimen.exercises, exercise.index, exercise);
      }
      this.unsaved_data = true;
      this.current_regimen_exercise_edit = this.blank_regimen_exercise_edit();
    },
    container_source_duplicate(candidate) {
      if (!["grid_sequence", "grid_set", "grid_schedule"].includes(candidate.exercise_type)) {
        return false;
      }
      return this.regimen.exercises.some(
        (exercise) =>
          exercise.exercise_type === candidate.exercise_type &&
          exercise.exercise_id === candidate.exercise_id &&
          exercise.uuid !== candidate.uuid,
      );
    },
    edit(index) {
      this.current_regimen_exercise_edit = this.regimen.exercises[index];
      this.current_regimen_exercise_edit.new = false;
      this.current_regimen_exercise_edit.index = index;
      this.allowed_exercise_types = undefined;
      this.dialog = true;
    },
    add(allowed_exercise_types) {
      this.current_regimen_exercise_edit = this.blank_regimen_exercise_edit();
      this.allowed_exercise_types = allowed_exercise_types;
      this.dialog = true;
    },
    blank_regimen_exercise_edit() {
      return {
        uuid: uuid.v4(),
        exercise_id: null,
        customization: "",
        instructions: "",
        config: {},
        level: 0,
        new: true,
        index: null,
        config_version: 0,
      };
    },
    load() {
      this.in_flight = true;
      // Get
      axios.get("/api/invite/" + this.invite_id + "/regimen").then((response) => {
        this.in_flight = false;
        let resp = response.data;
        if (resp.status == "error") {
          messageBus.$emit("api-error", resp);
        } else {
          if (resp.data) {
            let regimen = resp.data;

            // Delete any exercises that don't have an exercise_type, or don't have an ID and are not custom or header
            // TODO: Determine HOW these badly formed exercises got into the db into the first place
            Vue.set(regimen, "exercises", regimen.exercises.filter((exercise) => {
              if (!exercise.exercise_type) {
                return false;
              }
              if (
                exercise.exercise_type != "custom" &&
                exercise.exercise_type != "header" &&
                exercise.exercise_type != "questionnaire" &&
                exercise.exercise_type != "question" &&
                exercise.exercise_type != "assessment" &&
                !exercise.exercise_id
              ) {
                return false;
              }
              return true;
            }));

            Vue.set(this, 'regimen', Object.assign({}, this.regimen, regimen));
          } else {
            this.regimen = {};
            Vue.set(this.regimen, "invite_id", this.invite_id);
            Vue.set(this.regimen, "clinic_id", this.clinic.id);
            Vue.set(this.regimen, "owner_id", this.invite_owner_id ? this.invite_owner_id : this.current_user.id);
            Vue.set(this.regimen, "instructions", "");
            Vue.set(this.regimen, "exercises", []);
            Vue.set(this.regimen, "permissions", {});
          }

          this.unsaved_data = false;
          // If ownership doesn't match, mark unsaved_data as true so we update the invite on save
          if (this.invite_owner_id && this.invite_owner_id != this.regimen.owner_id) {
            this.unsaved_data = true;
          }
        }
      });
    },
    discard_changes() {
      if (confirm(this.$t("regimen_edit.discard_changes") + "?")) {
        this.load();
      }
    },
    submit: function () {
      this.error_message = "";

      // If the exercises don't yet have a uuid, give them a uuid
      for (let exercise of this.regimen.exercises) {
        if (!exercise.uuid) {
          exercise.uuid = uuid.v4();
        }
      }

      // Remove all .config from video exercises
      // TODO: Remove this at some point in the future when it's irrelevant
      for (let exercise of this.regimen.exercises) {
        if (exercise.exercise_type == "video/builtin" || exercise.exercise_type == "video/clinic" || exercise.exercise_type == "video/public") {
          exercise.config = {};
        }
      }

      // Embedded schedule assignments must not keep a removed stage id.
      for (let exercise of this.regimen.exercises) {
        if (exercise.exercise_type === "grid_schedule") {
          ensureValidGridScheduleStage(exercise);
        }
      }

      this.in_flight = true;
      if (this.regimen.id) {
        // Update
        axios.put("/api/regimen/" + this.regimen.id, this.regimen).then((response) => {
          this.in_flight = false;
          let resp = response.data;
          if (resp.status == "error") {
            messageBus.$emit("api-error", resp);
          } else {
            this.unsaved_data = false;
            messageBus.$emit("success", this.$t("regimen_edit.exercises_updated"));
            this.$emit("regimen-updated", this.regimen);
            this.$emit("close");
          }
        });
      } else {
        // Create
        axios.post("/api/regimen", this.regimen).then((response) => {
          this.in_flight = false;
          let resp = response.data;
          if (resp.status == "error") {
            messageBus.$emit("api-error", resp);
          } else {
            this.unsaved_data = false;
            messageBus.$emit("success", this.$t("regimen_edit.exercises_updated"));
            this.$emit("regimen-created", this.regimen);
            this.regimen.id = resp.data;
            this.$emit("close");
          }
        });
      }
    },
  },
};
</script>

